import { onMounted, onUnmounted } from 'vue';
import type { PriceStyle, SiteStyle } from '@/interface/enum';
import { LanguageType } from '@/interface/enum';
import type { PipStatus } from '@/libraries/composables/usePipState';
import { usePipState } from '@/libraries/composables/usePipState';
import { setI18nLanguage } from '@/libraries/i18n';
import { useAccountStore } from '@/stores/accountStore';
import { jsonParse } from '@/stores/helpers/featureParsers';

const FLUTTER_EVENT = 'flutterChannel' as const;

enum FlutterAction {
    UpdateCustomerInfo = 'update_customer_info',
    UpdateLanguage = 'update_language',
    UpdatePipStatus = 'update_pip_status',
}

export type FlutterPayload = {
    action: FlutterAction;
    message?: string;
};

type FlutterCustomerInfo = {
    siteStyle: SiteStyle;
    priceStyle: PriceStyle;
    isAcceptAnyOdds: boolean;
    isAcceptAnyCashOutValue: boolean;
}

const languageTypeMap: Record<string, LanguageType> = {
    EN: LanguageType.EN,
    ZH_CN: LanguageType.ZH_CN,
    JA_JP: LanguageType.JA_JP,
    KO_KR: LanguageType.KO_KR,
    TH_TH: LanguageType.TH_TH,
    VI_VN: LanguageType.VI_VN,
    ID_ID: LanguageType.ID_ID,
    MY_MM: LanguageType.MY_MM,
    TA_IN: LanguageType.TA_IN,
    HI_IN: LanguageType.HI_IN,
    PT_BR: LanguageType.PT_BR,
    KM_KH: LanguageType.KM_KH,
    BN_BD: LanguageType.BN_BD,
    MS_MY: LanguageType.MS_MY,
};

export function useFlutterChannel() {
    function updateCustomerInfo(message: string) {
        const newCustomerInfo = jsonParse<FlutterCustomerInfo>(message);

        if (newCustomerInfo) {
            const { setSiteStyle, setPriceStyle, setIsAcceptAnyOdds, setIsAcceptAnyCashOutValue } = useAccountStore();
            setSiteStyle(newCustomerInfo.siteStyle);
            setPriceStyle(newCustomerInfo.priceStyle);
            setIsAcceptAnyOdds(newCustomerInfo.isAcceptAnyOdds);
            setIsAcceptAnyCashOutValue(newCustomerInfo.isAcceptAnyCashOutValue);
        }
    }

    const { updatePipStatus } = usePipState();

    async function flutterChannelHandler(e: Event) {
        const ce = e as CustomEvent<FlutterPayload>;

        switch (ce.detail.action) {
            case FlutterAction.UpdateCustomerInfo:
                updateCustomerInfo(ce.detail.message ?? '');
                break;
            case FlutterAction.UpdateLanguage:
                await setI18nLanguage(languageTypeMap[ce.detail.message ?? 'EN'] ?? LanguageType.EN);
                window.location.reload();
                break;
            case FlutterAction.UpdatePipStatus: {
                const pipStatus = (ce.detail as { message?: PipStatus }).message;
                if (pipStatus && typeof pipStatus.isPipMode === 'boolean') {
                    updatePipStatus(pipStatus);
                }
                break;
            }
            default:
                console.error('unknown flutter action', ce.detail.action);
                break;
        }
    }

    onMounted(() => {
        window.addEventListener(FLUTTER_EVENT, flutterChannelHandler);
    });

    onUnmounted(() => {
        window.removeEventListener(FLUTTER_EVENT, flutterChannelHandler);
    });

    return {
        flutterChannelHandler,
        updateCustomerInfo,
    };
}
